import { useState, useEffect, useCallback, useMemo } from "react";
import * as global from '../data/global';
import { MuiMaterials } from "../@index";
import { outlinedActionButtonSx } from './windowChrome/index.js';

const setTemperatureLabel = async () =>  {
    if (global.gw.selectedSite.selectedMark.sensorHistories && global.gw.selectedSite.selectedMark.sensorHistories.length > 0) {
        for (let i = 0; i < global.gw.selectedSite.selectedMark.sensorHistories.length; i++) {
            let sensorHistory = global.gw.selectedSite.selectedMark.sensorHistories[i];
            global.updateSensorTemperature(sensorHistory.binId, sensorHistory.cableId, sensorHistory.sensorId, sensorHistory.temperature, sensorHistory.lastTemperature);
        }

        if (global.gw.selectedSite.isObjectSelected) {
            global.setUnselectedCablesOff();
        }
        else {
            if (global.gw.selectedSite.isCablesOff) {
                global.setAllBinsTextColorOn();
            }
            else {
                global.setAllBinsTextColorOff();
            }
        }
    }
}

export const Comparison = (props) => {
    const [loading, setLoading] = useState(false);
    const [forceUpdate, setForceUpdate] = useState(0);

    // Helper function to get theme-aware colors (reads directly from global.gw.theme)
    const getThemeColor = useCallback((lightColor, darkColor) => {
        if (global.gw.theme === 'light') {
            return lightColor;
        }
        return darkColor;
    }, []);

    // Listen for theme changes - only trigger re-render when theme actually changes
    useEffect(() => {
        const handleThemeChange = () => {
            setForceUpdate(prev => prev + 1);
        };
        window.addEventListener('themeChanged', handleThemeChange);
        return () => window.removeEventListener('themeChanged', handleThemeChange);
    }, []);

    const theme = useMemo(() => {
        const isLight = global.gw.theme === 'light';
        return MuiMaterials.createTheme({
            components: {
                MuiInputLabel: {
                    styleOverrides: {
                        root: {
                            color: isLight ? '#000000' : '#f4cc8e',
                            fontSize: "18px",
                            ...(isLight && {
                                '&:not(.MuiInputLabel-shrink)': {
                                    top: '50%',
                                    transform: 'translateY(-50%)',
                                    left: '14px',
                                },
                            }),
                        },
                        shrink: {
                            top: isLight ? '8px' : undefined,
                        },
                    }
                },
                MuiButton: {
                    styleOverrides: {
                        root: {
                            fontSize: "18px",
                            background: isLight ? '#e0e0e0' : '#2d2d2d',
                            color: isLight ? '#333333' : 'rgb(244, 204, 142)',
                        }
                    }
                },
                MuiIconButton: {
                    styleOverrides: {
                        sizeMedium: {
                            color: isLight ? '#333333' : 'rgb(244, 204, 142)'
                        }
                    }
                },
                MuiOutlinedInput: {
                    styleOverrides: {
                        root: {
                            color: isLight ? '#333333' : '#f4cc8e',
                            "& .MuiOutlinedInput-notchedOutline": {
                                border: `0.5px solid ${isLight ? '#cccccc' : '#f4cc8e4d'}`,
                            },
                            "&.Mui-focused": {
                                "& .MuiOutlinedInput-notchedOutline": {
                                    border: `0.5px solid ${isLight ? '#666666' : '#f4cc8e'}`,
                                },
                            },
                            "&:hover": {
                                "& .MuiOutlinedInput-notchedOutline": {
                                    border: `0.5px solid ${isLight ? '#666666' : '#f4cc8e'}`,
                                },
                            },
                        }
                    }
                },
                MuiTextField: {
                    styleOverrides: {
                        root: {
                            ".MuiInputLabel-root.Mui-focused": {
                                color: isLight ? '#000000' : '#f4cc8e',
                            },
                        },
                    }
                },
            },
        });
    }, [forceUpdate]);

    return (
        <MuiMaterials.Box
            className="comparison" 
            sx={{
                display: "none",
                position: "absolute",
                top: "10%",
                left: "50%",
            }}
        >
            <MuiMaterials.Box
                key={`comparison-box-${global.gw.theme}-${forceUpdate}`}
                sx={{
                    position: "relative",
                    top: "-50%",
                    left: "-50%",
                    background: getThemeColor('#ffffff', 'rgb(45 45 45)'),
                    borderColor: getThemeColor('#cccccc', 'rgb(244, 204, 142)'),
                    borderStyle: "solid",
                    borderWidth: "1px",
                    padding: "15px",
                    display: "flex",
                    flexDirection: "column",
                }}
            >
                <MuiMaterials.ThemeProvider theme={theme} key={`comparison-theme-${global.gw.theme}-${forceUpdate}`}>
                    <MuiMaterials.FormControlLabel
                        sx={{
                            color: getThemeColor('#333333', '#f4cc8e'),
                            margin: "auto auto 15px 10px",
                        }}
                        control={<MuiMaterials.Checkbox
                            key={`comparison-checkbox-${global.gw.theme}-${forceUpdate}`}
                            sx={{
                                color: getThemeColor('#333333', '#f4cc8e'),
                                '&.Mui-checked': {
                                    color: getThemeColor('#333333', '#f4cc8e'),
                                },
                            }}
                            onChange={(e) => {
                                let tvcs = window.document.getElementsByClassName("cmp");
                                if (global.gw.selectedSite.isComparisonOn) {
                                    global.gw.selectedSite.isComparisonOn = false;
                                    tvcs[0].style.color = global.gw.theme === 'light' ? "rgba(51, 51, 51, 0.38)" : "rgba(244, 204, 142, 0.38)";
                                }
                                else {
                                    global.gw.selectedSite.isComparisonOn = true;
                                    tvcs[0].style.color = global.gw.theme === 'light' ? "rgb(51, 51, 51)" : "rgb(244, 204, 142)";
                                }
                                setTemperatureLabel();
                                global.toggleComparison();
                            }}
                            name={"chbComparison" }
                        />}
                        label="Comparison"
                    />
                    <MuiMaterials.TextField
                        key={`comparison-plus-${global.gw.theme}-${forceUpdate}`}
                        className="plusElement"
                        label="Plus"
                        sx={{
                            margin: "5px",
                            background: getThemeColor('#ffffff', '#2d2d2d'),
                            color: getThemeColor('#333333', '#f4cc8e'),
                            '& .MuiInputLabel-root': {
                                color: getThemeColor('#000000', '#f4cc8e'),
                                top: getThemeColor('8px', '0'),
                            },
                            '& .MuiInputLabel-root.Mui-focused': {
                                color: getThemeColor('#000000', '#f4cc8e'),
                                top: getThemeColor('8px', '0'),
                            },
                            '& .MuiOutlinedInput-root': {
                                '& fieldset': {
                                    borderColor: getThemeColor('#cccccc', '#f4cc8e'),
                                },
                                '&:hover fieldset': {
                                    borderColor: getThemeColor('#666666', '#f4cc8e'),
                                },
                                '&.Mui-focused fieldset': {
                                    borderColor: getThemeColor('#666666', '#f4cc8e'),
                                },
                            },
                        }}
                        InputProps={{
                            style: {
                                fontSize: "18px",
                                background: getThemeColor('#ffffff', '#2d2d2d'),
                                color: getThemeColor('#333333', '#f4cc8e'),
                            }
                        }}
                        inputProps={{
                            style: {
                                padding: "14px 5px",
                                width: "158px",
                                textAlign: "center",
                            }
                        }}
                        defaultValue={global.gw.selectedSite.main.plus}
                        onFocus={() => {
                            let plus = window.document.querySelector('.plusElement .MuiInputBase-input');
                            if (plus) {
                                plus.select();
                            }
                        }}
                    />
                    <MuiMaterials.TextField
                        key={`comparison-minus-${global.gw.theme}-${forceUpdate}`}
                        className="minusElement"
                        label="Minus"
                        sx={{
                            margin: "5px",
                            background: getThemeColor('#ffffff', '#2d2d2d'),
                            color: getThemeColor('#333333', '#f4cc8e'),
                            '& .MuiInputLabel-root': {
                                color: getThemeColor('#000000', '#f4cc8e'),
                                top: getThemeColor('8px', '0'),
                            },
                            '& .MuiInputLabel-root.Mui-focused': {
                                color: getThemeColor('#000000', '#f4cc8e'),
                                top: getThemeColor('8px', '0'),
                            },
                            '& .MuiOutlinedInput-root': {
                                '& fieldset': {
                                    borderColor: getThemeColor('#cccccc', '#f4cc8e'),
                                },
                                '&:hover fieldset': {
                                    borderColor: getThemeColor('#666666', '#f4cc8e'),
                                },
                                '&.Mui-focused fieldset': {
                                    borderColor: getThemeColor('#666666', '#f4cc8e'),
                                },
                            },
                        }}
                        InputProps={{
                            style: {
                                fontSize: "18px",
                                background: getThemeColor('#ffffff', '#2d2d2d'),
                                color: getThemeColor('#333333', '#f4cc8e'),
                            }
                        }}
                        inputProps={{
                            style: {
                                padding: "14px 5px",
                                width: "158px",
                                textAlign: "center",
                            }
                        }}
                        defaultValue={global.gw.selectedSite.main.minus}
                        onFocus={() => {
                            let minus = window.document.querySelector('.minusElement .MuiInputBase-input');
                            if (minus) {
                                minus.select();
                            }
                        }}
                    />
                    <MuiMaterials.LoadingButton
                        key={`comparison-button-${global.gw.theme}-${forceUpdate}`}
                        type="button"
                        loading={loading}
                        variant="outlined"
                        size="small"
                        sx={{
                            margin: "8px 5px",
                            width: "160px",
                            ...outlinedActionButtonSx(),
                        }}
                        onClick={(e) => {
                            setLoading(true);
                            let plus = window.document.querySelector('.plusElement .MuiInputBase-input');
                            if (plus) {
                                global.gw.selectedSite.main.plus = plus.value;
                            }
                            let minus = window.document.querySelector('.minusElement .MuiInputBase-input');
                            if (minus) {
                                global.gw.selectedSite.main.minus = minus.value;
                            }
                            e.preventDefault();
                            if (global.gw.selectedSite.isComparisonOn) {
                                setTemperatureLabel();
                            }
                            setLoading(false);
                            global.toggleComparison();
                            return false;
                        }}
                    >
                        Apply
                    </MuiMaterials.LoadingButton>
                </MuiMaterials.ThemeProvider>
            </MuiMaterials.Box>
        </MuiMaterials.Box>
    );
}

export default Comparison;